import { useMemo, useState } from 'react'

import { IconCaretDown } from '@tabler/icons-react'
import { Skeleton, Space, Table, Typography } from 'antd'
import { Key } from 'antd/lib/table/interface'
import moment from 'moment'
import styled from 'styled-components'

import { calculateHours } from '../CustomBlocks/WorkerAttendance/workerAttendanceHelpers'

import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import TitleColumn from '@/components/shared-components/TitleColumn'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { TimezonedDate } from '@/context/timezoneContext'
import { ManualSignOns, useAttendanceData } from '@/hooks/useAttendanceData'
import { WorkerAttendanceDisplayBlock as WorkerAttendanceDisplayBlockType } from '@/modules/templates/types/blueprint'
import { pluralise, pluraliseWord } from '@/utils/pluralise'

import { Subcontractor } from 'types/api.graphql'

const ShowWorkers = styled(Typography.Text)`
  font-size: 0.75rem;
  margin-left: 1rem;
`

// todo hugh - does this work still?
const OpenCloseIcon = styled(IconCaretDown).attrs({ size: 12 })`
  & > svg {
    transition: 0.4s;
  }
`

type PdfData = {
  manuallySuppliedSignOns: ManualSignOns
  manuallySuppliedSubcontractors: [Subcontractor]
}

type Props = {
  block: WorkerAttendanceDisplayBlockType
}

export const WorkerAttendanceDisplayBlock: React.FC<Props> = ({ block }) => {
  const { extraInfo, projectId } = useTemplateRenderer()

  const siteDiaryId = extraInfo.siteDiaryId as string
  const dataForPDF = extraInfo.dataForPDF as PdfData

  const [expandedRows, setExpandedRows] = useState<Key[]>()

  const { loading, importedAttendance, projectSubcontractors, projectWorkers } =
    useAttendanceData(
      projectId,
      siteDiaryId,
      block.value,
      block.hourConfig,
      dataForPDF
        ? {
            includeSubbieBreakdown: false,
            manuallySuppliedSignOns: dataForPDF.manuallySuppliedSignOns,
            manuallySuppliedSubcontractors:
              dataForPDF.manuallySuppliedSubcontractors,
          }
        : undefined
    )

  const hasSubbieComments = useMemo(() => {
    return importedAttendance?.subcontractorAttendances?.some(
      (s) => !!s.comments
    )
  }, [importedAttendance])

  if (loading) return <Skeleton />

  const subbieName = (id: string) =>
    projectSubcontractors.find((s) => s.id === id)?.company?.name || 'Unknown'

  const workerName = (id: string) =>
    projectWorkers.find((w) => w.id === id)?.name || 'Unknown'

  const autoComments = (id: string) =>
    block?.value?.overrides?.find((o) => o.workerId === id)?.comments

  const subbieColumns = [
    {
      title: 'Company',
      key: 'name',
      dataIndex: 'name',
      render: (subbie: string, record) => {
        const numWorkers = record.workerAttendance?.length || 0
        const isExpanded = expandedRows?.includes(record.key)

        return (
          <Typography.Text>
            {subbie}
            {dataForPDF ? null : (
              <ShowWorkers type={isExpanded ? 'success' : 'secondary'}>
                <OpenCloseIcon rotate={isExpanded ? 0 : 180} />{' '}
                {isExpanded ? ' Hide' : ' Show'}{' '}
                {pluraliseWord(numWorkers, 'Worker')} ({numWorkers})
              </ShowWorkers>
            )}
          </Typography.Text>
        )
      },
    },
    {
      title: 'Hours',
      key: 'hours',
      dataIndex: 'hours',
    },
    ...(hasSubbieComments
      ? [
          {
            width: 400,
            title: 'Comments',
            key: 'comments',
            dataIndex: 'comments',
            render: (_, record) => (
              <Text noVariant style={{ whiteSpace: 'pre-wrap' }}>
                {record.comments}
              </Text>
            ),
          },
        ]
      : []),
  ]

  const workerColumns = [
    {
      title: 'Worker',
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: 'Signed On',
      dataIndex: 'signedOnAt',
      render: (dateTime, record) => {
        const isSameDay = moment(dateTime).isSame(new Date(), 'day')

        return dateTime ? (
          <TitleColumn
            title={<TimezonedDate date={dateTime} includeDate={!isSameDay} />}
            subtitle={record.isManualSignedOnAt ? 'Manual sign on' : null}
          />
        ) : (
          '-'
        )
      },
    },
    {
      title: 'Signed Off',
      dataIndex: 'signedOffAt',
      render: (dateTime, record) => {
        const isSameDay = moment(dateTime).isSame(new Date(), 'day')

        return dateTime ? (
          <TitleColumn
            title={<TimezonedDate date={dateTime} includeDate={!isSameDay} />}
            subtitle={record.isManualSignedOffAt ? 'Manual sign off' : null}
          />
        ) : (
          '-'
        )
      },
    },
    {
      title: 'Comments',
      key: 'workType',
      dataIndex: 'workType',
      render: (workType) =>
        Array.isArray(workType) ? workType.join(', ') : workType,
    },
    {
      title: 'Hours',
      key: 'hours',
      dataIndex: 'hours',
    },
  ]

  const subbieData = importedAttendance.subcontractorAttendances
    ?.filter(
      (s) =>
        !!s.comments || s.workerAttendance?.some((w) => w.workerId && w.hours)
    )
    .map((s, i1) => ({
      key: `${s.subcontractorId}-${i1}`,
      name: subbieName(s.subcontractorId),
      comments: s.comments,
      hours: pluralise(calculateHours(s, block?.value?.overrides || []), 'hr'),
      hoursRaw: calculateHours(s, block?.value?.overrides || []),
      workerAttendance: s.workerAttendance
        ?.filter((w) => w.workerId && w.hours)
        .map((w, i2) => ({
          key: `${w.workerId}-${i2}`,
          name: workerName(w.workerId),
          hours: pluralise(w.hours, 'hr'),
          workType: w.manuallyCreated ? w.workType : autoComments(w.workerId),
          signedOnAt: w.signedOnAt,
          signedOffAt: w.signedOffAt,
          isManualSignedOffAt: w.isManualSignedOffAt,
        })),
    }))

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Table
          scroll={dataForPDF ? undefined : { x: 'max-content' }}
          pagination={false}
          columns={subbieColumns}
          dataSource={subbieData}
          expandable={{
            showExpandColumn: dataForPDF ? false : true,
            expandRowByClick: true,
            onExpandedRowsChange: (expandedRows: Key[]) =>
              setExpandedRows(expandedRows),
            expandedRowRender: (record) => (
              <Table
                bordered={false}
                pagination={false}
                columns={workerColumns}
                dataSource={record.workerAttendance}
              />
            ),
            defaultExpandAllRows: dataForPDF ? true : false,
            rowExpandable: () => true,
          }}
        />

        {!!importedAttendance.visitors?.length && (
          <Flexbox justifyContent="space-between" alignItems="center">
            <div style={{ margin: '1rem 0' }}>
              <label htmlFor="visitors">Visitors</label>
              <ul id="visitors">
                {importedAttendance.visitors.map((visitor) => (
                  <li key={visitor}>{visitor}</li>
                ))}
              </ul>
            </div>
          </Flexbox>
        )}
      </Space>
    </>
  )
}
